import {
  Band,
  entityScopeFromFilters,
  QueueDecisionState,
  QueueFilters,
  QueueSortKey,
  QueueSubjectFilter,
} from '../../models';

// URL ↔ QueueFilters round-tripping for shareable, refresh-stable links.
// Not the same wire format as toUrlSearchParams() in models/queueItem.ts —
// that one targets the backend's /queue endpoint; this one targets the
// browser URL bar. The two diverge intentionally (backend wants PascalCase
// decisionState; the URL stays camelCase for readability).

const BAND_VALUES: readonly Band[] = ['autoApprove', 'humanReview', 'autoBlock'];
const SUBJECT_VALUES: readonly QueueSubjectFilter[] = ['pages', 'blobs'];
const SORT_VALUES: readonly QueueSortKey[] = ['scoreDesc', 'ageDesc', 'ageAsc'];
const DECISION_STATE_VALUES: readonly Exclude<QueueDecisionState, null>[] = ['undecided', 'decided'];

function parseEnum<T extends string>(
  raw: string | null,
  allowed: readonly T[],
  fallback: T,
): T {
  return raw != null && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

function parseInRange(raw: string | null, fallback: number, min: number, max: number): number {
  if (raw == null) return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}

// Accept only positive integers — fractional ("12.5"), zero, and negative
// values are silently discarded. Mirrors the guard we put on `params.id` in
// ReviewEventScreen: a deep-link with a bogus value shouldn't fire requests
// for a rounded / non-existent ID.
function parseOptionalInt(raw: string | null): number | null {
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function parseDecisionState(raw: string | null): QueueDecisionState {
  if (raw == null) return null;
  if (raw === '') return null;
  return (DECISION_STATE_VALUES as readonly string[]).includes(raw)
    ? (raw as Exclude<QueueDecisionState, null>)
    : null;
}

// The view selector pairs a band with a decisionState. When a URL only sets
// `band=` (no decisionState=), pick the natural decisionState for that band
// so the view chip preselects on load — autoApprove/autoBlock don't carry a
// decisionState, only humanReview does (the work-pile vs. decided split).
function defaultDecisionStateForBand(
  band: Band,
  humanReviewFallback: QueueDecisionState,
): QueueDecisionState {
  return band === 'humanReview' ? humanReviewFallback : null;
}

export function filtersFromUrlSearchParams(
  params: URLSearchParams,
  fallback: QueueFilters,
): QueueFilters {
  const fundraiserId = parseOptionalInt(params.get('fundraiserId'));
  const teamId = parseOptionalInt(params.get('teamId'));
  const collectionId = parseOptionalInt(params.get('collectionId'));
  const band = parseEnum(params.get('band'), BAND_VALUES, fallback.band);

  return {
    band,
    subjectFilter: parseEnum(params.get('subject'), SUBJECT_VALUES, fallback.subjectFilter),
    minScore: parseInRange(params.get('minScore'), fallback.minScore, 1, 10),
    maxScore: parseInRange(params.get('maxScore'), fallback.maxScore, 1, 10),
    decisionState: params.has('decisionState')
      ? parseDecisionState(params.get('decisionState'))
      : defaultDecisionStateForBand(band, fallback.decisionState),
    linkedAccountId: parseOptionalInt(params.get('linkedAccountId')),
    linkedOrganizationId: parseOptionalInt(params.get('linkedOrganizationId')),
    fundraiserId,
    teamId,
    collectionId,
    sort: parseEnum(params.get('sort'), SORT_VALUES, fallback.sort),
    page: 1,
    pageSize: fallback.pageSize,
  };
}

export function filtersToUrlSearchParams(filters: QueueFilters): URLSearchParams {
  const params = new URLSearchParams();
  const scope = entityScopeFromFilters(filters);

  if (scope) {
    // Entity-history mode: keep the URL clean — just the entity id and sort.
    params.set(`${scope.kind}Id`, String(scope.id));
    params.set('sort', filters.sort);
    return params;
  }

  params.set('band', filters.band);
  params.set('subject', filters.subjectFilter);
  params.set('minScore', String(filters.minScore));
  params.set('maxScore', String(filters.maxScore));
  params.set('sort', filters.sort);
  if (filters.decisionState !== null) {
    params.set('decisionState', filters.decisionState);
  }
  if (filters.linkedAccountId !== null) {
    params.set('linkedAccountId', String(filters.linkedAccountId));
  }
  if (filters.linkedOrganizationId !== null) {
    params.set('linkedOrganizationId', String(filters.linkedOrganizationId));
  }
  return params;
}

// Compare two URLSearchParams instances for set-equality (independent of key
// insertion order). Used to short-circuit no-op URL writes that would
// otherwise cause router re-renders.
export function urlSearchParamsEqual(a: URLSearchParams, b: URLSearchParams): boolean {
  const aSorted = Array.from(a.entries()).sort();
  const bSorted = Array.from(b.entries()).sort();
  if (aSorted.length !== bSorted.length) return false;
  for (let i = 0; i < aSorted.length; i += 1) {
    if (aSorted[i][0] !== bSorted[i][0]) return false;
    if (aSorted[i][1] !== bSorted[i][1]) return false;
  }
  return true;
}
